import React, { useState } from 'react';
import { aboutPillars } from '../data/mock';

const CARD_THEMES = [
  {
    bg: '#ffffff',
    textPrimary: '#000000',
    textSecondary: 'rgba(0,0,0,0.6)',
    indexColor: 'rgba(0,0,0,0.2)',
    overlayColor: 'rgba(255,255,255,0.90)',
    overlayColorHover: 'rgba(255,255,255,0.30)',
    accentBar: '#C8102E',
  },
  {
    bg: '#C8102E',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.80)',
    indexColor: 'rgba(255,255,255,0.25)',
    overlayColor: 'rgba(200,16,46,0.88)',
    overlayColorHover: 'rgba(200,16,46,0.28)',
    accentBar: '#ffffff',
  },
  {
    bg: '#1B3D2A',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.75)',
    indexColor: 'rgba(255,255,255,0.25)',
    overlayColor: 'rgba(27,61,42,0.90)',
    overlayColorHover: 'rgba(27,61,42,0.28)',
    accentBar: '#C8102E',
  },
];

const AboutSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <section id="about" style={{
      backgroundColor: '#000',
      padding: '120px 0',
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 40px' }}>
        {/* Section Label */}
        <div style={{ marginBottom: '64px' }}>
          <span style={{
            color: 'rgba(255,255,255,0.4)',
            fontFamily: "'Figtree', sans-serif",
            fontSize: '12px',
            fontWeight: '600',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            display: 'block',
            marginBottom: '20px',
          }}>About</span>
          <h2 style={{
            color: '#ffffff',
            fontFamily: "'Climate Crisis', sans-serif",
            fontSize: 'clamp(32px, 4vw, 52px)',
            fontWeight: '400',
            letterSpacing: '0em',
            lineHeight: '1.1',
            margin: '0',
            maxWidth: '600px',
          }}>
            What I Stand for
          </h2>
        </div>

        {/* Three Colored Pillar Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
        }} className="pillars-grid">
          {aboutPillars.map((pillar, index) => {
            const theme = CARD_THEMES[index];
            const isHovered = hoveredCard === index;

            return (
              <div
                key={pillar.id}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  position: 'relative',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  minHeight: '520px',
                  cursor: 'default',
                  transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
                  boxShadow: isHovered ? '0 32px 72px rgba(0,0,0,0.55)' : 'none',
                  transition: 'transform 0.4s ease, box-shadow 0.4s ease',
                  backgroundColor: theme.bg,
                }}
              >
                {/* Background sport image */}
                <img
                  src={pillar.image}
                  alt=""
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: isHovered ? 0.75 : (index === 0 ? 0.08 : 0.12),
                    transform: isHovered ? 'scale(1.06)' : 'scale(1)',
                    transition: 'opacity 0.5s ease, transform 0.6s ease',
                  }}
                />

                {/* Color overlay — fades on hover to let image through */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: isHovered ? theme.overlayColorHover : theme.overlayColor,
                  transition: 'background-color 0.5s ease',
                }} />

                {/* Bottom dark gradient on hover for text legibility */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: isHovered
                    ? 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 55%, transparent 100%)'
                    : 'transparent',
                  transition: 'background 0.5s ease',
                }} />

                {/* Content */}
                <div style={{
                  position: 'relative',
                  zIndex: 2,
                  padding: '44px 40px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: '520px',
                  boxSizing: 'border-box',
                }}>
                  {/* Top: index */}
                  <div style={{
                    color: isHovered ? 'rgba(255,255,255,0.5)' : theme.indexColor,
                    fontFamily: "'Figtree', sans-serif",
                    fontSize: '13px',
                    fontWeight: '700',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    transition: 'color 0.4s ease',
                  }}>
                    0{index + 1}
                  </div>

                  {/* Bottom: title + description */}
                  <div>
                    {/* Accent bar */}
                    <div style={{
                      width: '36px',
                      height: '3px',
                      backgroundColor: theme.accentBar,
                      borderRadius: '2px',
                      marginBottom: '20px',
                    }} />
                    <h3 style={{
                      color: isHovered ? '#ffffff' : theme.textPrimary,
                      fontFamily: "'Climate Crisis', sans-serif",
                      fontSize: '28px',
                      fontWeight: '400',
                      letterSpacing: '0em',
                      marginBottom: '16px',
                      lineHeight: '1.15',
                      transition: 'color 0.4s ease',
                    }}>
                      {pillar.title}
                    </h3>
                    <p style={{
                      color: isHovered ? 'rgba(255,255,255,0.85)' : theme.textSecondary,
                      fontFamily: "'Figtree', sans-serif",
                      fontSize: '15px',
                      fontWeight: '400',
                      lineHeight: '1.75',
                      margin: 0,
                      transition: 'color 0.4s ease',
                    }}>
                      {pillar.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .pillars-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default AboutSection;
